import axios from 'axios';

// إعداد axios
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// إضافة interceptor للتوكن - يستخدم نفس التوكن المحفوظ في النظام
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    // إضافة التوكن في كلا الصيغتين للتوافق مع middleware المختلفة
    config.headers.Authorization = `Bearer ${token}`;
    config.headers['x-auth-token'] = token;
  }
  return config;
});

// أنواع البيانات
export interface IInvoicePayment {
  _id: string;
  invoiceId: string;
  invoiceNumber: string;
  paymentDate: string;
  accountId: string;
  accountName: string;
  accountNumber?: string;
  amount: number;
  description?: string;
  transactionId: string;
  branchId: string;
  createdBy: string;
  status: 'active' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

export interface IAccount {
  _id: string;
  name: string;
  accountNumber?: string;
  currentBalance: number;
  accountType: string;
  isActive: boolean;
}

export interface IInvoiceRemainingDetails {
  invoice: {
    _id: string;
    invoiceNumber: string;
    total: number;
    status: string;
    notes?: string;
    cashPaidAmount?: number;
    isPartialPayment?: boolean;
    sourceInvoiceId?: string;
  };
  remainingAmount: number;
  totalPaidFromAccounts: number;
  payments: IInvoicePayment[];
}

export interface IPaymentRequest {
  accountId: string;
  amount: number;
  description?: string;
}

export interface IPaymentResponse {
  success: boolean;
  message: string;
  data: {
    payment: IInvoicePayment;
    transaction: any;
    remainingAmount: number;
    invoiceStatus: string;
  };
}

// جلب الحسابات المتاحة للتسديد
export const getAvailableAccounts = async (): Promise<IAccount[]> => {
  try {
    const response = await api.get('/invoice-payments/accounts');
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching available accounts:', error);
    throw new Error(error.response?.data?.message || 'حدث خطأ أثناء جلب الحسابات');
  }
};

// جلب تفاصيل الفاتورة مع المبلغ المتبقي
export const getInvoiceRemainingDetails = async (invoiceId: string): Promise<IInvoiceRemainingDetails> => {
  try {
    const response = await api.get(`/invoice-payments/invoice/${invoiceId}/details`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching invoice remaining details:', error);
    throw new Error(error.response?.data?.message || 'حدث خطأ أثناء جلب تفاصيل الفاتورة');
  }
};

// جلب سدادات فاتورة معينة
export const getInvoicePayments = async (invoiceId: string): Promise<IInvoicePayment[]> => {
  try {
    const response = await api.get(`/invoice-payments/invoice/${invoiceId}`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching invoice payments:', error);
    throw new Error(error.response?.data?.message || 'حدث خطأ أثناء جلب سدادات الفاتورة');
  }
};

// تسديد المبلغ المتبقي من الفاتورة
export const payRemainingAmount = async (invoiceId: string, paymentData: IPaymentRequest): Promise<IPaymentResponse> => {
  try {
    const response = await api.post(`/invoice-payments/invoice/${invoiceId}/pay`, paymentData);
    return response.data;
  } catch (error: any) {
    console.error('Error paying remaining amount:', error);
    console.error('Error details:', {
      response: error.response,
      data: error.response?.data,
      message: error.response?.data?.message,
      status: error.response?.status
    });
    
    // التحقق من نوع الخطأ وإرجاع رسالة مناسبة
    if (error.response?.data?.message) {
      // إذا كانت الرسالة تحتوي على معلومات عن تجاوز المبلغ المتبقي
      if (error.response.data.message.includes('أكبر من المبلغ المتبقي')) {
        throw new Error('المبلغ المدفوع أكبر من المتبقي');
      }
      // رسائل أخرى من الخادم
      throw new Error(error.response.data.message);
    }
    
    // التحقق من حالة الخطأ
    if (error.response?.status === 500) {
      throw new Error('المبلغ المدفوع أكبر من المتبقي');
    }
    
    // رسالة احتياطية محسنة
    throw new Error('المبلغ المدفوع أكبر من المتبقي');
  }
};

// إلغاء سداد
export const cancelPayment = async (paymentId: string): Promise<any> => {
  try {
    const response = await api.delete(`/invoice-payments/${paymentId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error cancelling payment:', error);
    throw new Error(error.response?.data?.message || 'حدث خطأ أثناء إلغاء السداد');
  }
};